import {
  IUserFieldsFragment,
  useCurrentUserQuery,
  useLoginUserMutation,
  useLogoutUserMutation,
  useRegisterUserMutation
} from "app/gql/graphqlSchema";
import {createContext, ReactNode, useContext, useMemo, useState} from "react";
import {assertNotNil} from "app/util/stdlib";

interface IGlobalState {
  user: IUserFieldsFragment | null;
  isLoggedIn: boolean;
  initialized: boolean;
  loginUser: ReturnType<typeof useLoginUserMutation>[0];
  registerUser: ReturnType<typeof useRegisterUserMutation>[0];
  logoutUser: ReturnType<typeof useLogoutUserMutation>[0];
}

const GlobalStateContext = createContext<IGlobalState | null>(null);

interface IProps {
  children?: ReactNode;
}

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  assertNotNil(context);
  return context;
};

export const GlobalStateProvider = ({children}: IProps) => {
  const [currentUser, setCurrentUser] = useState<IUserFieldsFragment | null>(null);
  const [initialized, setInitialized] = useState(false);
  useCurrentUserQuery({
    onCompleted: (data) => {
      setCurrentUser(data.currentUser ?? null);
      setInitialized(true);
    }
  });

  const [loginUser] = useLoginUserMutation({
    onCompleted: (data) => {
      setCurrentUser(data.loginUser.user ?? null);
    }
  });

  const [registerUser] = useRegisterUserMutation({
    onCompleted: (data) => {
      setCurrentUser(data.registerUser.user ?? null);
    }
  });

  const [logoutUser] = useLogoutUserMutation({
    onCompleted: (data) => {
      if (data.logoutUser.success) {
        setCurrentUser(null);
      }
    }
  });

  const contextValue = useMemo(
    () => ({
      user: currentUser,
      isLoggedIn: currentUser !== null,
      initialized,
      loginUser,
      registerUser,
      logoutUser
    }),
    [currentUser, initialized, loginUser, logoutUser, registerUser]
  );

  return <GlobalStateContext.Provider value={contextValue}>{children}</GlobalStateContext.Provider>;
};
